import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hashPassword } from '../../utils/auth';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiEye, FiEyeOff, FiHome } = FiIcons;

const Login = ({ onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const hashedPassword = hashPassword(formData.password);
      const response = await apiService.searchUsers({
        mail: formData.email,
        password: hashedPassword
      });

      if (response.data && response.data.length > 0) {
        const user = response.data[0];
        
        // Remove password immediately so it doesn't propagate
        if (user.password) delete user.password;

        // Check if user has a team
        const userTeamResponse = await apiService.getUserTeamRelations({
          user_id: user.id
        });

        const hasTeam = userTeamResponse.data && userTeamResponse.data.length > 0;
        const teamId = hasTeam ? userTeamResponse.data[0].team_id : null;

        login({
          ...user,
          hasTeam,
          teamId
        });

        if (!hasTeam) {
          navigate('/team-setup');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError('Ungültige Anmeldedaten');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8 relative">
      
      {/* Top Right Link to Landing Page */}
      <div className="absolute top-4 right-4 sm:top-8 sm:right-8">
        <Link 
          to="/public/landing" 
          className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors font-medium px-4 py-2 rounded-full hover:bg-blue-50"
        >
          <SafeIcon icon={FiHome} className="w-5 h-5" />
          <span className="hidden sm:inline">Zur Startseite</span>
        </Link>
      </div>

      {/* Application Header & Info Section */}
      <div className="text-center mb-10 max-w-3xl px-4">
        <img 
          src="https://efs-techhub.com/wp-content/uploads/2025/04/image-4054.png" 
          alt="Techhub by EFS Logo" 
          className="h-24 w-auto object-contain mx-auto mb-8"
        />
        <h1 style={{
      color: 'rgb(17, 24, 39)'
    }} className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">Team Collaboration Hub</h1>
        <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
          Die zentrale Plattform für nachhaltige Teamentwicklung. 
          Analysieren Sie Stärken, identifizieren Sie Potenziale und setzen Sie gemeinsam datenbasierte Handlungsempfehlungen um.
        </p>
      </div>

      {/* Login Card */}
      <div className="max-w-md w-full space-y-8 bg-gray-50 p-8 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col items-center border-b border-gray-200 pb-6">
          <h2 className="text-center text-2xl font-bold text-gray-900">
            Willkommen zurück
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Bitte melden Sie sich an, um fortzufahren
          </p>
        </div>
        
        <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm flex items-start">
              <span className="font-medium mr-1">Fehler:</span> {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail-Adresse
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiMail} className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
                  placeholder="ihre@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Passwort
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiLock} className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
                  placeholder="Passwort eingeben"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-bold rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
            >
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </button>
          </div>
          
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              Noch kein Konto? Jetzt registrieren
            </button>
          </div>
        </form>
      </div>
      
      <div className="mt-8 text-center text-xs text-gray-400">
        &copy; {new Date().getFullYear()} EFS TechHub. Alle Rechte vorbehalten.
      </div>
    </div>
  );
};

export default Login;